import classNames from "classnames";
import { Question } from "components/Question/Question";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState, useRef } from "react";
import "./index.css";
import { ORGANIZATIONS } from "constant";
import { storage } from "services/storage";
import { OverlayRectangle } from "assets/svg";
import { useLanguageContext } from "store/LanguageProvider";
import { GC } from "services/gamecentreService";

/** Wizr addition: Gridbox inside circle with fading effect around edges -> SplashScreen (Only wizr change -> SplashScreen with images)*/
const CustomGridBox = ({ elemId }) => {
  const createGrid = () => {
    const gridContainer = document.getElementById(`${elemId}`);
    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 5; j++) {
        const gridItem = document.createElement('div');
        gridItem.classList.add('grid-item');
        gridContainer.appendChild(gridItem);
      }
    }
  }

  useEffect(() => { if (elemId) createGrid() }, [elemId]);

  /** Custom classes handled css file */
  return (
    <div className="custom-grid-box">
      <div className="grid-container" id={elemId}></div>
      <div className="circle"></div>
    </div>
  )
};

/** Wizr addition */
const ImageColumn = ({ imageUrl }) => {
  return (
    <div className="relative sm:order-2 sm:block sm:h-[400px] sm:w-[600px]">
      <img
        src={imageUrl}
        className={classNames(
          "absolute sm:left-[10%] md:left-[15%] lg:left-[20%] top-[-10%] h-[360px] w-[274px] object-fit rotate-[12deg]",
          "stack-box-shadow z-10"
        )}
      />
      <div className="relative back-panel sm:w-[90%] sm:h-full bg-[#E1D7C9] rounded-2xl overflow-clip">
        <div className="absolute left-[110px] top-[190px]">
          <CustomGridBox elemId={1} />
        </div>
        <div className="absolute left-[360px] top-[110px]">
          <CustomGridBox elemId={2} />
        </div>
      </div>
    </div>
  );
};

// Using this component for both splash screena and section intermediate screenn
export const SplashScreen = ({
  heading,
  description,
  onContinue,
  questionsCount,
  quizTime,
  imageUrl, //= "https://images.unsplash.com/photo-1672710257583-d814ae0ac7c9?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=687&q=80",

  sectionBreaker = false, // will be true for intermediate section screen
  question,
  options,
  type,
  media = [],
  optionSubmitHandler,
  isQuizEnded = false,
  isMcq = false,
  multiChatQuiz = false,
}) => {

  const organization = storage.get.org();
  const parentApp = storage.get.parentApp()
  const wizrFlag = organization === ORGANIZATIONS.WIZR;
  const spotlightOrgFlag = organization === ORGANIZATIONS.SPOTLIGHT
  const isParentGC = parentApp === "GC"
  const [loading, setLoading] = useState(false);

  const { rtlView, direction } = useLanguageContext();

  /** 
   * Track if the heading text is overflowing.
   * Req - Add/remove classes from headingClass if heading overflows top of left portion container
   * **/
  const outerLeftContainerRef = useRef(null);
  const innerheadingContainerRef = useRef(null);
  const [isHeadingTextOverflowing, setIsHeadingTextOverflowing] = useState(false);

  const checkTopOutOfContainer = () => {
    const outerContainer = outerLeftContainerRef.current;
    const innerContainer = innerheadingContainerRef.current;

    if (outerContainer && innerContainer) {
      const isTopOutOfView = innerContainer.offsetTop < outerContainer.scrollTop;
      setIsHeadingTextOverflowing(isTopOutOfView);
    }
  };

  useEffect(() => {
    checkTopOutOfContainer();
    window.addEventListener('resize', checkTopOutOfContainer);

    return () => window.removeEventListener('resize', checkTopOutOfContainer);
  }, []);

  useEffect(() => {
    if (isQuizEnded) {
      onContinue()
    }
  }, [isQuizEnded])

  /** **/

  const timeObj = useMemo(() => {
    if (!!quizTime) {
      if (quizTime < 3600) {
        // seconds less than 1hr so convert to minutes - round off
        const time = quizTime / 60;
        return { time: (Math.round(time)), unit: "min" };
      } else {
        // else convert to hours
        const time = quizTime / 3600;
        return {
          time: time.toFixed(quizTime % 3600 === 0 ? 0 : 1),
          unit: "hr",
        };
      }
    }
    // To hide the time if quizTime is falsy value
    return quizTime;
  }, [quizTime]);

  useEffect(() => {
    if (isMcq && spotlightOrgFlag) {
      document.body.style.fontFamily = "Martian Mono SemiExpanded"
    }
  }, [isMcq])

  const headingClass = classNames(
    { "heading": !wizrFlag && !spotlightOrgFlag },
    { "heading-wizr": wizrFlag },
    "my-1.5 font-medium tracking-[-0.5px]",
    "sm:pb-2 sm:text-[65px] sm:leading-[67px]",
    {
      "text-2xl": !!sectionBreaker,
      "text-[45px] leading-[57px]": !sectionBreaker,
    },
    {
      "text-[#fff] text-[37px] font-normal uppercase mt-[100px] text-left mb-[15px]": spotlightOrgFlag && isMcq
    },
    {
      "text-right sm:w-3/4 short:w-full flex items-end": rtlView && imageUrl
    },
  );
  const descriptionClass = classNames(
    "text-lg leading-[30px] tracking-[-0.5px]",
    "sm:flex sm:w-3/4 sm:text-xl",
    {
      "sm:justify-start": !!imageUrl,
      "sm:justify-center": !imageUrl,
    },
    "short:w-full",
    {
      "font-normal": !wizrFlag,
      "text-[#2B1911] font-medium leading-[30px] tracking-[-0.04]": wizrFlag
    },
    {
      "text-start text-[16px] font-extralight leading-[24px] text-[#fff] opacity-70": spotlightOrgFlag && isMcq,
      "text-parchment": !(spotlightOrgFlag && isMcq),
      "hidden": spotlightOrgFlag && isMcq && isQuizEnded
    },
    {
      "sm:text-[28px] text-[24px]": rtlView,
      "!text-right-ar-large": rtlView && !!imageUrl
    }
  );
  const buttonClass = classNames(
    "sm:py-[10px] sm:text-lg",
    {
      // avoiding extra spacing for breaker screen
      "sm:px-16": !sectionBreaker,
    },
    {
      "text-black-pearl": !wizrFlag,
      "bg-[#1D1B21] text-white sm:text-white font-inter": wizrFlag
    },
    {
      "cursor-not-allowed opacity-50": loading
    },
    {
      "bg-[#fff] py-[23px] px-[100px] lowercase text-[15px] text-[#000] font-light": spotlightOrgFlag && isMcq && !sectionBreaker,
      "rounded-full bg-buddha-gold py-[10px] px-7 text-left": !(spotlightOrgFlag && isMcq && !sectionBreaker)
    }
  );

  const questionsCountAndTime = (<div className={
    classNames(
      { "flex gap-6 p-3": !wizrFlag },
      { "inline-flex px-7 py-[22px] items-start gap-6 rounded-xl bg-[#E1D7C9] backdrop-blur-12": wizrFlag },
      {
        "text-[#fff] mt-[100px]": spotlightOrgFlag && isMcq,
        "border-2 border-buddha-gold-tint rounded-xl text-parchment": !(spotlightOrgFlag && isMcq)
      }
    )
  }>
    {!!questionsCount && (
      <div className={classNames("flex ", {
        "items-start flex-col-reverse border-l-2 border-l-[#414243] pl-[10px]": spotlightOrgFlag && isMcq,
        "items-center flex-col": !(spotlightOrgFlag && isMcq)
      })}>
        <div className={classNames("font-medium",
          { "text-sm": !wizrFlag },
          { "text-base text-[#1D1B21]": wizrFlag },
          { "text-[16px] font-extralight opacity-70": spotlightOrgFlag && isMcq }
        )}>
          {multiChatQuiz ? "#Tickets" : "Questions"}
        </div>
        <div className={classNames("text-3xl font-medium",
          wizrFlag ? "text-[#1D1B21] opacity-60" : "",
          {
            "text-[28px]": spotlightOrgFlag && isMcq
          })} >{questionsCount}</div>
      </div>
    )}
    {!!questionsCount && !!timeObj && !(spotlightOrgFlag && isMcq) && (
      <div className="h-full w-[2px] bg-buddha-gold-tint"></div>
    )}
    {!!timeObj && (
      <div className={classNames("flex ", {
        "items-start flex-col-reverse border-l-2 border-l-[#414243] pl-[10px] ml-[30px]": spotlightOrgFlag && isMcq,
        "items-center flex-col": !(spotlightOrgFlag && isMcq)
      })}>
        <div className={classNames("font-medium",
          { "text-sm": !wizrFlag },
          { "text-base text-[#1D1B21]": wizrFlag },
          { "text-[16px] font-extralight opacity-70": spotlightOrgFlag && isMcq }
        )}>
          Time
        </div>
        <div className={classNames("text-3xl font-medium",
          wizrFlag ? "text-[#1D1B21] opacity-60" : "",
          {
            "text-[28px]": spotlightOrgFlag && isMcq
          })}>
          {timeObj.time}
          <span
            className={
              classNames("text-sm",
                { "text-lg text-[#1D1B21] ml-1 font-bold tracking-wide opacity-60": wizrFlag })}
          >
            {timeObj.unit}
          </span>
        </div>
      </div>
    )}
  </div>)

  /**
   * Destroy timeLeft from LS if splash screen is rendered (except breaker case) as timer resumption
   * is not needed if user has either visited to next quiz or ended the current quiz or 
   * not attempted any question.
   * After this flow will go to setQuizData in Home.jsx (line 122) where lastTimeLeft will be null 
   * due to which timeLeft will be set as per API response.
   */
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!sectionBreaker) {
      storage.destroy.timeRemaining()
    }
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={classNames(
        "flex h-full flex-col items-center justify-between ",
        "sm:flex-row sm:p-0",
        {
          "bg-[#F0EAE4]": wizrFlag
        },
        { "h-auto sm:h-full": !!imageUrl && !(spotlightOrgFlag && isMcq) },
        {
          "bg-[#000] h-[100vh] overflow-y-auto overflow-x-hidden relative": (spotlightOrgFlag && isMcq),
          "p-5": !(spotlightOrgFlag && isMcq)
        }
      )}
    >
      {
        spotlightOrgFlag && isMcq
          ? <>
            <span className={classNames("absolute z-[1] h-[100vh] overflow-y-hidden left-[0px] top-[0px] opacity-[.05]")}>
              <OverlayRectangle />
            </span>
            <div className={classNames("w-[70vh] h-[70vh] bg-[#181a1b] absolute rounded-full top-[100px] right-[-45vh]")} />
          </>
          : <></>
      }
      <div
        dir={!isQuizEnded && direction ? direction : 'ltr'}
        className={classNames(
          {
            "sm:items-start sm:text-left": !!imageUrl,
          },
          "z-[1] order-2 flex flex-1 h-full flex-col justify-between gap-10 py-0 px-0 text-center",
          "sm:order-1 sm:flex-[1.2]  sm:gap-20 sm:px-9 sm:py-16",
          "short:gap-10 short:py-0 short:pl-0 short:sm:px-9",
          "breaker-screen-scroll",
          {
            "sm:justify-center": !sectionBreaker && !isHeadingTextOverflowing,
            "overflow-scroll sm:justify-start": sectionBreaker,
            "sm:overflow-scroll": isHeadingTextOverflowing
          },
          {
            "items-start": spotlightOrgFlag && isMcq && !sectionBreaker,
            "items-center": !(spotlightOrgFlag && isMcq && !sectionBreaker)
          }
        )}
        ref={outerLeftContainerRef}
      >
        {sectionBreaker ? (
          <div className="sm:order-1">
            <Question
              breakerScreen
              className={question.length > 80 ? descriptionClass : headingClass}
              text={question}
              type={type}
              noMedia={!media?.length}
            />
          </div>
        ) : (
          <div
            className={classNames(
              { "sm:items-start": !!imageUrl, "sm:items-center": !imageUrl },
              { "flex flex-col gap-4": !spotlightOrgFlag },
              { "flex flex-col items-start p-5": spotlightOrgFlag && isMcq }
            )}
            ref={innerheadingContainerRef}
          >
            <h1 className={headingClass}>{heading}</h1>
            {!!description && (
              <>
                {/* <p
                className={classNames(
                  "mt-4 text-xl font-medium text-parchment",
                  "sm:mt-8 sm:text-2xl"
                )}
              >
                🎯 Instructions
              </p> */}
                <p dir="auto" className={descriptionClass}>
                  {typeof description === "string" ? description : ""}
                </p>
              </>
            )}
            {
              (spotlightOrgFlag && isMcq)
                ? questionsCountAndTime
                : <></>
            }
          </div>
        )}
        {(!!questionsCount || !!timeObj) && (
          !(spotlightOrgFlag && isMcq)
            ? questionsCountAndTime
            : <></>
        )}
        {sectionBreaker ? (
          <div className="flex flex-col items-center gap-2 sm:items-start sm:order-2">
            {options.map(({ id, text }) => (
              <button
                onClick={() => optionSubmitHandler({ optionId: id }, true, false, true)}
                key={id}
                className={buttonClass}
              >
                {text}
              </button>
            ))}
          </div>
        ) : (
          <div className={classNames("flex w-full flex-col items-center", {
            "pb-[10px]": spotlightOrgFlag && isMcq && !sectionBreaker,
            "hidden": spotlightOrgFlag && isMcq && isQuizEnded && !isParentGC,
            "sm:items-start": !!imageUrl
          })} >
            <button
              className={buttonClass}
              onClick={() => {
                if (isQuizEnded) {
                  setLoading(true);
                  GC.sendGameUnmountMessage()
                } else {
                  onContinue()
                }
              }}
              disabled={loading}
            >
              Continue
            </button>
            {loading &&
              <p
                className={classNames(
                  "text-lg leading-[30px] tracking-[-0.5px] text-parchment",
                  "sm:text-base mt-2 text-center"
                )}
              >
                Submitting ...
              </p>
            }
          </div>

        )}
      </div>
      {!!imageUrl && !wizrFlag && (
        <div
          className={classNames(
            `order-1 flex-1 min-h-[250px] w-full rounded-[20px] bg-contain bg-center bg-no-repeat mb-8`,
            "sm:order-2 sm:block sm:h-full sm:flex-1 sm:rounded-[20px] sm:bg-cover sm:mb-0",
            {
              hidden: !sectionBreaker,
            },
            // Image as bg overlay on mobile
            // "absolute inset-0 z-0 h-full w-full bg-cover bg-center bg-no-repeat",
            // "after:absolute after:inset-0 after:block after:bg-[#000A]",
            // "sm:static sm:order-2 sm:block sm:flex-1 sm:rounded-[20px] sm:bg-cover sm:after:content-none"
          )}
          style={{ backgroundImage: `url('${imageUrl}')` }}
        ></div>
      )}
      {
        !!imageUrl && wizrFlag && <ImageColumn imageUrl={imageUrl} />
      }
    </motion.div>
  );
};
